import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fuzzyFilterStrings } from '../../utils/fuzzy';

export function FilterDrawer({
  isOpen,
  onClose,
  sortBy = 'lastName',
  onSortByChange,
  departments = [],
  selectedDepartments = [],
  onToggleDepartment,
  employeeCountByDept = {},
  offices = [],
  selectedOffices = [],
  onToggleOffice,
  employeeCountByOffice = {},
  smartGroups = [],
  selectedSmartGroup,
  onSelectSmartGroup,
  onClearAll,
}) {
  const [deptSearch, setDeptSearch] = useState('');
  const [officeSearch, setOfficeSearch] = useState('');
  const panelRef = useRef(null);

  // Click-anywhere-outside-to-close — the drawer has no backdrop (it sits
  // inline beside the list, not over it), so this is the only way to
  // dismiss it besides the toggle button itself. The toggle button lives
  // outside this panel's DOM subtree and handles its own open/close, so
  // clicks on it (marked with data-filter-toggle) are excluded here to avoid
  // fighting that handler.
  useEffect(() => {
    if (!isOpen || !onClose) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-filter-toggle]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  const totalActive =
    selectedDepartments.length + selectedOffices.length + (selectedSmartGroup ? 1 : 0);

  const filteredDepts = fuzzyFilterStrings(departments, deptSearch);

  const filteredOffices = fuzzyFilterStrings(offices, officeSearch);

  return (
    <AnimatePresence>
      {isOpen && (
          /* Inline side panel — sits beside the directory list rather than
              covering it, so Select all / view controls / clear filters on
              the tab row stay reachable and filtered results update live as
              selections are made. Sticky so it stays in view while the
              (possibly long) list scrolls underneath it.
              Animating `width` (not just opacity/x) is what makes the
              directory list reflow smoothly in step with the drawer instead
              of sitting frozen until unmount and then snapping back — the
              outer element is the width-animated clipping mask, the inner
              one keeps the card's own fixed-width look intact. */
          <motion.div
            ref={panelRef}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="hidden lg:block flex-none sticky top-4 max-h-[calc(100vh-2rem)] overflow-hidden"
          >
            <div className="w-[320px] h-full flex flex-col rounded-card border border-line shadow-e1 bg-surface overflow-hidden">

            {/* ── Header ── */}
            <div className="relative flex items-center justify-between px-6 py-5 border-b border-line flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-accent-soft ring-1 ring-accent-line flex items-center justify-center">
                  <SlidersIcon className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-ink tracking-wide">Filters & Sort</h2>
                  <p className={`text-[11px] transition-colors ${totalActive > 0 ? 'text-accent-ink' : 'text-ink-4'}`}>
                    {totalActive > 0 ? `${totalActive} active` : 'No filters active'}
                  </p>
                </div>
              </div>

              <AnimatePresence>
                {totalActive > 0 && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    onClick={onClearAll}
                    className="text-[11px] text-ink-3 hover:text-ink transition-colors px-2.5 py-1.5 rounded-lg hover:bg-surface-2 font-medium"
                  >
                    Clear all
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto relative" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--line-strong) transparent' }}>

              {/* Sort */}
              <Section label="Sort by">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'lastName', label: 'Last name' },
                    { value: 'firstName', label: 'First name' },
                    // Also reachable from the list column header. Under these
                    // the A–Z rail hides, since letters would be arbitrary.
                    { value: 'department', label: 'Department' },
                    { value: 'officeLocation', label: 'Office' },
                    { value: 'tenure', label: 'Tenure' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => onSortByChange(value)}
                      className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                        sortBy === value
                          ? 'bg-accent-soft text-accent-ink ring-1 ring-accent-line'
                          : 'bg-surface-2 text-ink-3 hover:text-ink'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Section>

              <Divider />

              {/* Smart Filters */}
              {smartGroups.length > 0 && (
                <>
                  <Section label="Smart Filters">
                    <div className="space-y-0.5">
                      {smartGroups.map((group) => {
                        const isActive = selectedSmartGroup === group.id;
                        return (
                          <FilterRow
                            key={group.id}
                            label={group.name}
                            count={group.count}
                            isActive={isActive}
                            onClick={() => onSelectSmartGroup(isActive ? null : group.id)}
                            type="radio"
                          />
                        );
                      })}
                    </div>
                  </Section>
                  <Divider />
                </>
              )}

              {/* Departments */}
              <Section
                label="Departments"
                badge={selectedDepartments.length || null}
              >
                {departments.length > 7 && (
                  <SearchInput
                    value={deptSearch}
                    onChange={setDeptSearch}
                    placeholder="Search departments…"
                  />
                )}
                <div className="space-y-0.5 max-h-56 overflow-y-auto pr-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--line-strong) transparent' }}>
                  {filteredDepts.map((dept) => (
                    <FilterRow
                      key={dept}
                      label={dept}
                      count={employeeCountByDept[dept] || 0}
                      isActive={selectedDepartments.includes(dept)}
                      onClick={() => onToggleDepartment(dept)}
                      type="checkbox"
                    />
                  ))}
                  {filteredDepts.length === 0 && (
                    <p className="text-sm text-ink-4 text-center py-6">No departments match</p>
                  )}
                </div>
              </Section>

              <Divider />

              {/* Offices */}
              <Section
                label="Offices"
                badge={selectedOffices.length || null}
              >
                {offices.length > 7 && (
                  <SearchInput
                    value={officeSearch}
                    onChange={setOfficeSearch}
                    placeholder="Search offices…"
                  />
                )}
                <div className="space-y-0.5 max-h-56 overflow-y-auto pr-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--line-strong) transparent' }}>
                  {filteredOffices.map((office) => (
                    <FilterRow
                      key={office}
                      label={office}
                      count={employeeCountByOffice[office] || 0}
                      isActive={selectedOffices.includes(office)}
                      onClick={() => onToggleOffice(office)}
                      type="checkbox"
                    />
                  ))}
                  {filteredOffices.length === 0 && (
                    <p className="text-sm text-ink-4 text-center py-6">No offices match</p>
                  )}
                </div>
              </Section>

              <div className="h-10" />
            </div>
            </div>
          </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Section({ label, badge, children }) {
  return (
    <div className="px-6 py-5">
      <div className="flex items-center justify-between mb-3.5">
        <p className="text-[10px] font-bold text-ink-4 uppercase tracking-[0.16em]">{label}</p>
        {badge > 0 && (
          <motion.span
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="text-[10px] font-semibold text-accent-ink bg-accent-soft px-2 py-0.5 rounded-full"
          >
            {badge} selected
          </motion.span>
        )}
      </div>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="mx-6 h-px bg-line" />;
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative mb-3">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-4 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-surface-2 border border-line rounded-lg pl-8 pr-8 py-2 text-sm text-ink placeholder-ink-4 focus:outline-none focus:border-accent-line transition-colors"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-4 hover:text-ink-2 transition-colors"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function FilterRow({ label, count, isActive, onClick, type }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all text-sm group ${
        isActive
          ? 'bg-accent-soft text-accent-ink'
          : 'text-ink-3 hover:bg-surface-2 hover:text-ink'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {type === 'checkbox' ? (
          <div className={`w-[15px] h-[15px] rounded flex-shrink-0 border transition-all ${
            isActive ? 'bg-accent border-accent' : 'border-line-strong group-hover:border-ink-4'
          } flex items-center justify-center`}>
            {isActive && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        ) : (
          <div className={`w-[15px] h-[15px] rounded-full flex-shrink-0 border-2 transition-all ${
            isActive ? 'border-accent bg-accent-soft' : 'border-line-strong group-hover:border-ink-4'
          } flex items-center justify-center`}>
            {isActive && <div className="w-1.5 h-1.5 rounded-full bg-accent" />}
          </div>
        )}
        <span className="truncate text-left">{label}</span>
      </div>
      <span className={`text-xs tabular-nums flex-shrink-0 ml-2 ${isActive ? 'text-accent-ink' : 'text-ink-4'}`}>
        {count}
      </span>
    </button>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function SlidersIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
